import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { TransactionType } from 'dashboard/models/transaction-type';
import { TransactionTypeGridLayout } from 'dashboard/models/transaction-type-grid-layout';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setTransactionType,
  setTransactionTypeGridLayouts,
} from 'dashboard/redux/slices/transaction-type';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export function loadTransactionType(id: string): DashboardThunkAction<Promise<void>> {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.getResource<TransactionType>(
      ApiEndpoint.TransactionType,
      id,
    );

    const transactionTypeGridLayouts =
      response.included?.filter(
        (relationship): relationship is TransactionTypeGridLayout =>
          relationship.type === ApiModel.TransactionTypeGridLayout,
      ) ?? [];

    dispatch(setTransactionTypeGridLayouts(transactionTypeGridLayouts));

    dispatch(setTransactionType(response.data));
  };
}

export function updateTransactionType(
  transactionType: TransactionType,
  deactivateFieldSchemaKeys: string[],
): DashboardThunkAction<Promise<void>> {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.updateResource<TransactionType>(
      {
        type: ApiModel.TransactionType,
        id: transactionType.id,
        attributes: {
          name: transactionType.attributes['name'],
          'field-schemas': transactionType.attributes['field-schemas'],
          'default-status-id': transactionType.attributes['default-status-id'],
        },
      },
      {
        meta: {
          'deactivate-field-schema-keys': deactivateFieldSchemaKeys,
        },
      },
    );

    dispatch(setTransactionType(response.data));
    dispatch(showMessage('Successfully updated transaction type.', MessageType.Success));
  };
}

async function updateTransactionTypeField(
  transactionType: TransactionType,
  fieldSchemaKey: string,
  action: string,
): Promise<TransactionType> {
  const response = await SessionClientDashboard.postResourceAction<
    TransactionType,
    {
      type: ApiModel.TransactionType;
      id: string;
      attributes: { 'field-key': string };
    }
  >(
    {
      type: ApiModel.TransactionType,
      id: transactionType.id,
      attributes: {
        'field-key': fieldSchemaKey,
      },
    },
    action,
  );

  return response.data;
}

export function deactivateTransactionTypeField(
  transactionType: TransactionType,
  fieldSchemaKey: string,
): DashboardThunkAction<Promise<void>> {
  return async (dispatch) => {
    const transactionTypeResponse = await updateTransactionTypeField(
      transactionType,
      fieldSchemaKey,
      'deactivate-field',
    );

    dispatch(setTransactionType(transactionTypeResponse));
    dispatch(showMessage('Successfully deactivated field.', MessageType.Success));
  };
}

export function activateTransactionTypeField(
  transactionType: TransactionType,
  fieldSchemaKey: string,
): DashboardThunkAction<Promise<void>> {
  return async (dispatch) => {
    const transactionTypeResponse = await updateTransactionTypeField(
      transactionType,
      fieldSchemaKey,
      'activate-field',
    );
    dispatch(setTransactionType(transactionTypeResponse));
    dispatch(showMessage('Successfully activated field.', MessageType.Success));
  };
}

export function setDefaultTransactionStatus(
  transactionTypeId: string,
  transactionStatusId: string,
): DashboardThunkAction<Promise<void>> {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.updateResource<TransactionType>(
      {
        type: ApiModel.TransactionType,
        id: transactionTypeId,
        attributes: {
          'default-status-id': transactionStatusId,
        },
      },
      { meta: { 'default-status-id': transactionStatusId } },
    );
    dispatch(setTransactionType(response.data));
    dispatch(showMessage('Successfully updated default transaction status.', MessageType.Success));
  };
}
